// src/contexts/CartContext.tsx

'use client'

import { CartItem } from '@/lib/interfaces/ordering'
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { safeGetItem, safeSetItem } from '@/lib/utils/localStorage'

// Define el tipo para el contexto
interface CartContextType {
  cart: CartItem[];
  addToCart: (item: CartItem) => void;
  removeFromCart: (itemId: string, variantType: string) => void;
  clearCart: () => void;
  updateQuantity: (itemId: string, variantType: string, quantity: number) => void;
}

interface CartProviderProps {
  slug: string
  children: ReactNode
}

// Crea el contexto con valores por defecto
const CartContext = createContext<CartContextType | undefined>(undefined)

// Crea el proveedor del contexto
export const CartProvider = ({ children, slug }: CartProviderProps) => {
  const [cart, setCart] = useState<CartItem[]>([])

  // Cargar el carrito desde localStorage al montar el componente
  useEffect(() => {
    const storedCart = safeGetItem(`cart-${slug}`)
    if (storedCart) {
      setCart(JSON.parse(storedCart))
    }
  }, [slug])

  // Guardar el carrito en localStorage cada vez que cambie
  useEffect(() => {
    safeSetItem(`cart-${slug}`, JSON.stringify(cart))
  }, [cart, slug])

  const addToCart = (item: CartItem) => {
    setCart((prevCart) => {
      // Busca si el mismo producto con la misma variante ya está en el carrito
      const existingItemIndex = prevCart.findIndex(
        (cartItem) => cartItem.id === item.id && cartItem.variant.type === item.variant.type
      )

      if (existingItemIndex !== -1) {
        // Si existe, incrementa la cantidad
        const updatedCart = [...prevCart]
        updatedCart[existingItemIndex].quantity += item.quantity
        return updatedCart
      }

      // Si no existe, lo añade al carrito
      return [...prevCart, item]
    })
  }

  const removeFromCart = (itemId: string, variantType: string) => {
    setCart((prevCart) =>
      prevCart.filter(
        (cartItem) => !(cartItem.id === itemId && cartItem.variant.type === variantType)
      )
    )
  }

  const clearCart = () => {
    setCart([])
  }

  const updateQuantity = (itemId: string, variantType: string, quantity: number) => {
    setCart((prevCart) =>
      prevCart.map((item) =>
        item.id === itemId && item.variant.type === variantType
          ? { ...item, quantity }
          : item
      )
    )
  }

  return (
    <CartContext.Provider value={{ cart, addToCart, removeFromCart, clearCart, updateQuantity }}>
      {children}
    </CartContext.Provider>
  )
}

// Hook para usar el contexto fácilmente
export const useCart = (): CartContextType => {
  const context = useContext(CartContext)
  if (!context) {
    throw new Error('useCart debe ser usado dentro de un CartProvider')
  }
  return context
}
